// Thumbnail helper — serve small, transformed images in grids instead of
// full-resolution originals.
//
// Supabase Storage public URLs can be rewritten to the on-the-fly image
// render (transform) endpoint, which resizes/re-encodes server-side and lets
// the browser negotiate webp. A 1–2 MB original becomes a few hundred KB (and
// less as webp), which is the single biggest bandwidth win for image grids.
//
// Anything that isn't a Supabase Storage public object URL — external retailer
// product images, `data:` URLs, already-transformed render URLs — is returned
// unchanged, so this is always safe to wrap around any `src`.

type ResizeMode = "cover" | "contain" | "fill";

interface ThumbnailOptions {
  width?: number;
  height?: number;
  quality?: number;
  resize?: ResizeMode;
}

const OBJECT_PATH = "/storage/v1/object/public/";
const RENDER_PATH = "/storage/v1/render/image/public/";

export const thumbnailUrl = (
  url: string | null | undefined,
  opts: ThumbnailOptions = {},
): string => {
  if (!url) return url ?? "";
  // Only Supabase Storage public object URLs support the render transform.
  if (!url.includes(OBJECT_PATH)) return url;

  const { width , height, quality = 70, resize } = opts;
  const base = url.replace(OBJECT_PATH, RENDER_PATH);

  const params = new URLSearchParams();
  if (width) params.set("width", String(width));
  if (height) params.set("height", String(height));
  if (quality) params.set("quality", String(quality));
  if (resize) params.set("resize", resize);

  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}${params.toString()}`;
};
